import { Page } from '@models/page';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useStaticValues } from '@stateManagement/StaticValuesContext';

interface PageVariantProviderProps {
    children: React.ReactNode;
    pageVariants: Page['variants'];
}

interface PageVariantContextValue {
    selectedPageVariant: string | null;
    changeSelectedPageVariant: (variant: string) => void;
    pageVariants: Page['variants'];
}

const VARIANT_QUERY_PARAM = 'v';

const PageVariantContext = createContext<PageVariantContextValue | undefined>(undefined);

export function getInitialSelectedVariant(pageVariants: Page['variants'], fullUrl: string): string | null {
    if (!pageVariants || pageVariants.length === 0) {
        return null;
    }

    let variantFromUrl: string | null = null;

    try {
        const cleanUrl = fullUrl.replace(/&amp;/g, '&');
        const url = new URL(cleanUrl);
        variantFromUrl = url.searchParams.get(VARIANT_QUERY_PARAM);
    } catch (error) {
        console.warn('Failed to parse URL:', fullUrl, error);
    }

    // If variant exists in URL and is valid, use it
    if (variantFromUrl && pageVariants.includes(variantFromUrl)) {
        return variantFromUrl;
    }

    // If variant doesn't exist in URL or is invalid, use first item
    return pageVariants[0];
}

const updateUrlWithVariant = (variant: string): void => {
    if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const encodedVariant = encodeURIComponent(variant);
        url.searchParams.set(VARIANT_QUERY_PARAM, encodedVariant);
        window.history.replaceState({}, '', url.toString());
    }
};

export const PageVariantProvider = ({ children, pageVariants }: PageVariantProviderProps): React.ReactElement => {
    const { fullUrl } = useStaticValues();
    const [selectedPageVariant, setSelectedPageVariant] = useState<string | null>(() =>
        getInitialSelectedVariant(pageVariants, fullUrl)
    );

    const changeSelectedPageVariant = useCallback((variant: string) => {
        setSelectedPageVariant(variant);
        updateUrlWithVariant(variant);
    }, []);

    const contextValue = useMemo(
        () => ({
            selectedPageVariant,
            changeSelectedPageVariant,
            pageVariants,
        }),
        [selectedPageVariant, pageVariants, changeSelectedPageVariant]
    );

    return <PageVariantContext value={contextValue}>{children}</PageVariantContext>;
};

export const usePageVariant = (): PageVariantContextValue => {
    const context = useContext(PageVariantContext);
    if (context === undefined) {
        throw new Error('usePageVariant must be used within a PageVariantProvider');
    }

    return context;
};
